
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function TestBuchen() {
  const [formData, setFormData] = useState({
    firmenname: '',
    ansprechpartner: '',
    email: '',
    telefon: '',
    adresse: '',
    produktart: '',
    produktgroesse: '',
    produktgewicht: '',
    versandvolumen: '',
    startdatum: '',
    datenuebertragung: '',
    datenuebertragungSonstiges: '',
    besondereAnforderungen: '',
    nachricht: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase.from('test_bookings').insert([
        {
          firmenname: formData.firmenname,
          // Fixed typo: use the correct key from state
          ansprechpartner: formData.ansprechpartner,
          email: formData.email,
          telefon: formData.telefon,
          adresse: formData.adresse,
          produktart: formData.produktart,
          produktgroesse: formData.produktgroesse,
          produktgewicht: formData.produktgewicht,
          versandvolumen: formData.versandvolumen,
          startdatum: formData.startdatum,
          datenuebertragung: formData.datenuebertragung,
          datenuebertragung_sonstiges: formData.datenuebertragungSonstiges,
          besondere_anforderungen: formData.besondereAnforderungen,
          nachricht: formData.nachricht,
        },
      ]);

      if (error) {
        console.error('Supabase error:', error);
        setSubmitStatus('error');
      } else {
        setSubmitStatus('success');
        // Scroll to top after a successful submission
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-48 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Test buchen
              </h1>
              <p className="text-xl text-gray-600">
                Füllen Sie das Formular aus und wir senden Ihnen alle
                Informationen für einen kurzfristigen Projektstart zu.
              </p>
            </div>

            {/* Erfolgsmeldung statt Formular */}
            {submitStatus === 'success' ? (
              <div className="bg-white p-12 rounded-xl shadow-lg border border-gray-200 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                  <i className="ri-checkbox-circle-fill text-4xl text-green-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-black mb-4">
                  Vielen Dank für Ihre Anfrage!
                </h2>
                <p className="text-gray-600 text-lg mb-8">
                  Wir haben Ihre Daten erhalten und senden Ihnen in Kürze alle
                  Informationen als Excel-Datei per E-Mail zu.
                </p>
                <a
                  href="/"
                  className="inline-flex items-center px-8 py-3 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: '#B80E10' }}
                >
                  <i className="ri-home-line mr-2"></i>
                  Zurück zur Startseite
                </a>
              </div>
            ) : (
              /* Form */
              <form
                id="test-booking-form"
                /* In JSX a boolean attribute must have an explicit value */
                data-readdy-form={true}
                onSubmit={handleSubmit}
                className="bg-white p-8 md:p-12 rounded-xl shadow-lg border border-gray-200"
              >
                {/* Firmendaten */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-gray-200">
                    Firmendaten
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="firmenname"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Firmenname *
                      </label>
                      <input
                        type="text"
                        id="firmenname"
                        name="firmenname"
                        value={formData.firmenname}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="Ihre Firma GmbH"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="ansprechpartner"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Ansprechpartner *
                      </label>
                      <input
                        type="text"
                        id="ansprechpartner"
                        name="ansprechpartner"
                        value={formData.ansprechpartner}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="Max Mustermann"
                      />
                    </div>
                  </div>
                </div>

                {/* Kontaktdaten */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-gray-200">
                    Kontaktdaten
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="info@ihre-firma.de"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="telefon"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="+49 123 456789"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="adresse"
                      className="block text-sm font-semibold text-black mb-2"
                    >
                      Adresse *
                    </label>
                    <input
                      type="text"
                      id="adresse"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                      placeholder="Musterstraße 123, 12345 Musterstadt"
                    />
                  </div>
                </div>

                {/* Produktinformationen */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-gray-200">
                    Produktinformationen
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="produktart"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Art des Produkts *
                      </label>
                      <input
                        type="text"
                        id="produktart"
                        name="produktart"
                        value={formData.produktart}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="z.B. Elektronik, Lebensmittel, Textilien"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="produktgroesse"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Größe *
                      </label>
                      <input
                        type="text"
                        id="produktgroesse"
                        name="produktgroesse"
                        value={formData.produktgroesse}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="z.B. 20x15x10 cm"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="produktgewicht"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Gewicht *
                      </label>
                      <input
                        type="text"
                        id="produktgewicht"
                        name="produktgewicht"
                        value={formData.produktgewicht}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="z.B. 500g"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="versandvolumen"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Versandvolumen (Pakete/Monat) *
                      </label>
                      <input
                        type="text"
                        id="versandvolumen"
                        name="versandvolumen"
                        value={formData.versandvolumen}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
                        placeholder="z.B. 500 Pakete"
                      />
                    </div>
                  </div>
                </div>

                {/* Projektdetails */}
                <div className="mb-10">
                  <h3 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-gray-200">
                    Projektdetails
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="startdatum"
                        className="block text-sm font-semibold text-black mb-2"
                      >
                        Gewünschtes Startdatum *
                      </label>
                      <input
                        type="date"
                        id="startdatum"
                        name="startdatum"
                        value={formData.startdatum}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Datenübertragung */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-black mb-3">
                      Datenübertragung per: *
                    </label>
                    <div className="space-y-3">
                      {[
                        'E-Mail',
                        'SFTP',
                        'Cloud',
                        'API',
                        'Brieftaube',
                        'Sonstiges',
                      ].map((option) => (
                        <label
                          key={option}
                          className="flex items-center cursor-pointer group"
                        >
                          <input
                            type="radio"
                            name="datenuebertragung"
                            value={option}
                            checked={formData.datenuebertragung === option}
                            onChange={handleChange}
                            required
                            className="w-5 h-5 cursor-pointer"
                            style={{ accentColor: '#B80E10' }}
                          />
                          <span className="ml-3 text-gray-700 group-hover:text-black transition-colors">
                            {option}
                          </span>
                        </label>
                      ))}
                    </div>

                    {/* Sonstiges Textfeld */}
                    {formData.datenuebertragung === 'Sonstiges' && (
                      <div className="mt-4 pl-8 animate-fadeIn">
                        <label
                          htmlFor="datenuebertragungSonstiges"
                          className="block text-sm font-semibold text-black mb-2"
                        >
                          Bitte beschreiben Sie, wie Sie Ihre Daten übermitteln
                          möchten: *
                        </label>
                        <textarea
                          id="datenuebertragungSonstiges"
                          name="datenuebertragungSonstiges"
                          value={formData.datenuebertragungSonstiges}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          rows={3}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none text-sm"
                          placeholder="Beschreiben Sie Ihre bevorzugte Methode der Datenübertragung..."
                        />
                        <div className="text-right text-xs text-gray-500 mt-1">
                          {formData.datenuebertragungSonstiges.length}/500
                          Zeichen
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="besondereAnforderungen"
                      className="block text-sm font-semibold text-black mb-2"
                    >
                      Besondere Anforderungen
                    </label>
                    <textarea
                      id="besondereAnforderungen"
                      name="besondereAnforderungen"
                      value={formData.besondereAnforderungen}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none text-sm"
                      placeholder="Gibt es spezielle Anforderungen an die Verpackung oder den Versand?"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.besondereAnforderungen.length}/500 Zeichen
                    </div>
                  </div>
                </div>

                {/* Nachricht */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-gray-200">
                    Ihre Nachricht
                  </h3>
                  <div>
                    <label
                      htmlFor="nachricht"
                      className="block text-sm font-semibold text-black mb-2"
                    >
                      Nachricht
                    </label>
                    <textarea
                      id="nachricht"
                      name="nachricht"
                      value={formData.nachricht}
                      onChange={handleChange}
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none text-sm"
                      placeholder="Teilen Sie uns weitere Details mit..."
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.nachricht.length}/500 Zeichen
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-10 py-4 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#B80E10' }}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Absenden
                        <i className="ri-send-plane-fill ml-2"></i>
                      </>
                    )}
                  </button>
                </div>

                {/* Status Messages (fallback – also rendered after successful submit when form stays visible) */}
                {submitStatus === 'error' && (
                  <div className="mt-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                    <div className="flex items-center text-red-800">
                      <i className="ri-error-warning-fill text-2xl mr-3"></i>
                      <div>
                        <p className="font-semibold">Ein Fehler ist aufgetreten</p>
                        <p className="text-sm mt-1">
                          Bitte versuchen Sie es erneut oder kontaktieren Sie uns
                          telefonisch.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
